import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Download, Mail, MessageCircle, Calendar, MapPin, Users, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const bookingId = searchParams.get('booking');
  const transactionId = searchParams.get('txn');

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/payment/status/${bookingId}`);
      if (response.data.success) {
        setBooking(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch booking details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading booking details...</div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Booking not found</h1>
          <Link to="/" className="text-yellow-400 hover:text-yellow-300">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500 rounded-full mb-6 animate-bounce">
            <CheckCircle size={48} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Booking Confirmed!
          </h1>
          <p className="text-xl text-gray-300">
            Your payment was successful. Get ready for the thrill of a lifetime!
          </p>
        </div>

        {/* Booking Details Card */}
        <div className="bg-gray-800 border-2 border-green-500 rounded-2xl overflow-hidden mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-500 p-6">
            <h2 className="text-2xl font-bold text-white mb-2">Booking Details</h2>
            <p className="text-green-100">Booking ID: <span className="font-mono font-bold">{booking.bookingId}</span></p>
          </div>

          {/* Details Grid */}
          <div className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Customer Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-yellow-400 flex items-center">
                  <Users size={20} className="mr-2" />
                  Customer Information
                </h3>
                <div className="space-y-2 text-gray-300">
                  <p><span className="text-gray-400">Name:</span> <span className="text-white font-medium">{booking.customerName || 'N/A'}</span></p>
                  <p><span className="text-gray-400">Email:</span> <span className="text-white">{booking.email || 'N/A'}</span></p>
                  <p><span className="text-gray-400">Phone:</span> <span className="text-white">{booking.phone || 'N/A'}</span></p>
                </div>
              </div>

              {/* Booking Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-yellow-400 flex items-center">
                  <Calendar size={20} className="mr-2" />
                  Jump Details
                </h3>
                <div className="space-y-2 text-gray-300">
                  <p><span className="text-gray-400">Location:</span> <span className="text-white font-medium">{booking.locationName || 'N/A'}</span></p>
                  <p><span className="text-gray-400">Date:</span> <span className="text-white">{booking.date ? new Date(booking.date).toLocaleDateString() : 'N/A'}</span></p>
                  <p><span className="text-gray-400">Number of Jumpers:</span> <span className="text-white">{booking.numberOfJumpers || 1}</span></p>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-bold text-yellow-400 flex items-center mb-4">
                <CreditCard size={20} className="mr-2" />
                Payment Information
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Total Amount</p>
                  <p className="text-2xl font-bold text-white">₹{booking.amount?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Paid Amount</p>
                  <p className="text-2xl font-bold text-green-400">₹{booking.paidAmount?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Status</p>
                  <p className="text-lg font-bold text-green-400 uppercase">{booking.status || 'Confirmed'}</p>
                </div>
              </div>
              {transactionId && (
                <div className="mt-4 text-sm text-gray-400">
                  <p>Transaction ID: <span className="text-white font-mono">{transactionId}</span></p>
                </div>
              )}
            </div>

            {/* Important Notes */}
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-bold text-yellow-400 mb-4">Important Information</h3>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-2 text-blue-300 text-sm">
                <p className="flex items-start">
                  <Mail size={16} className="mr-2 mt-1 flex-shrink-0" />
                  A confirmation email has been sent to your registered email address
                </p>
                <p className="flex items-start">
                  <MessageCircle size={16} className="mr-2 mt-1 flex-shrink-0" />
                  You will receive a WhatsApp confirmation shortly
                </p>
                <p className="flex items-start">
                  <Calendar size={16} className="mr-2 mt-1 flex-shrink-0" />
                  Please arrive 30 minutes before your scheduled time
                </p>
                <p className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                  Carry a valid ID proof and this booking confirmation
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-lg transition-colors border-2 border-gray-700"
          >
            <Download size={20} className="mr-2" />
            Download Receipt
          </button>
          <Link
            to="/"
            className="flex items-center justify-center px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
          >
            Return to Homepage
          </Link>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12 text-gray-400 text-sm">
          <p>Need help? Contact us at <a href="mailto:support@indyabungy.com" className="text-yellow-400 hover:text-yellow-300">support@indyabungy.com</a></p>
          <p className="mt-2">For any queries regarding your booking, please quote your Booking ID</p>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
